import { Button } from '@/components/commons';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import { CloseOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { useEffect, useState } from 'react';
import type { IInviteCodeRow } from './types';

const CSV_MAX_SIZE = 10 * 1024 * 1024;
const CSV_INPUT_ID = 'company-create-csv-file-input';

interface ICsvUploadModalProps {
  open: boolean;
  onClose: () => void;
  onRegister: (rows: IInviteCodeRow[]) => void | Promise<void>;
}

const REQUIRED_HEADERS = [
  'id',
  'code',
  'valid_from',
  'valid_to',
  'cog_evo',
  'nounow',
  'limit',
] as const;

function parseCsvFile(
  file: File,
): Promise<{ headers: string[]; records: Record<string, string>[] }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length === 0) {
        resolve({ headers: [], records: [] });
        return;
      }
      const headers = lines[0].split(',').map((h) => h.trim());
      const records: Record<string, string>[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map((v) => v.trim());
        const row: Record<string, string> = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx] ?? '';
        });
        records.push(row);
      }
      resolve({ headers, records });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file, 'UTF-8');
  });
}

function getCsvVal(
  record: Record<string, string>,
  key: string,
): string | null {
  const k = Object.keys(record).find(
    (x) => x.toLowerCase().trim() === key.toLowerCase(),
  );
  const raw = k ? record[k] : undefined;
  const trimmed = (raw ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

function getCsvNum(
  record: Record<string, string>,
  key: string,
): number | null {
  const v = getCsvVal(record, key);
  if (v === null) return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

function mapCsvRecordToRow(
  record: Record<string, string>,
  index: number,
): IInviteCodeRow {
  const idKey = Object.keys(record).find(
    (x) => x.toLowerCase().trim() === 'id',
  );
  const idCellRaw = idKey ? String(record[idKey] ?? '') : null;
  const idCellTrimmed = idCellRaw === null ? null : idCellRaw.trim();
  const idNum =
    idCellTrimmed === null || idCellTrimmed === ''
      ? null
      : Number.parseInt(idCellTrimmed, 10);
  const hasIdColumn = idKey !== undefined;
  const hasIdValue = idCellTrimmed !== null && idCellTrimmed !== '';
  const limitRaw = getCsvVal(record, 'limit');
  const limitNum = getCsvNum(record, 'limit');
  const cogEvoRaw = getCsvVal(record, 'cog_evo');
  const cogEvoNum = getCsvNum(record, 'cog_evo');
  const nounowRaw = getCsvVal(record, 'nounow');
  const nounowNum = getCsvNum(record, 'nounow');

  return {
    // Auto index only when id column is absent.
    // If id column exists but empty/space/invalid, keep as invalid for error table.
    id: hasIdColumn ? (Number.isNaN(idNum) ? null : idNum) : index,
    id_raw:
      hasIdColumn && (Number.isNaN(idNum) || !hasIdValue)
        ? idCellRaw
        : null,
    code: getCsvVal(record, 'code'),
    valid_from: getCsvVal(record, 'valid_from'),
    valid_to: getCsvVal(record, 'valid_to'),
    cog_evo: cogEvoNum,
    cog_evo_raw: cogEvoRaw !== null && cogEvoNum === null ? cogEvoRaw : null,
    nounow: nounowNum,
    nounow_raw: nounowRaw !== null && nounowNum === null ? nounowRaw : null,
    limit: limitNum,
    limit_raw: limitRaw !== null && limitNum === null ? limitRaw : null,
  };
}

export const CsvUploadModal: React.FC<ICsvUploadModalProps> = ({
  open,
  onClose,
  onRegister,
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCsvFile(null);
      setHeaderError(null);
    }
  }, [open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      notificationMessage({
        message: '対応形式は .csv です。',
        type: NOTIFY_STATUS.ERROR,
      });
      return;
    }
    if (file.size > CSV_MAX_SIZE) {
      notificationMessage({
        message: '最大ファイルサイズは10MBです。',
        type: NOTIFY_STATUS.ERROR,
      });
      return;
    }
    setHeaderError(null);
    setCsvFile(file);
    e.target.value = '';
  };

  const handleRegister = async () => {
    if (!csvFile) return;
    setIsLoading(true);
    try {
      const { headers, records } = await parseCsvFile(csvFile);
      const normalizedHeaders = headers.map((h) => h.trim());
      const isValidHeaders =
        normalizedHeaders.length === REQUIRED_HEADERS.length &&
        REQUIRED_HEADERS.every((h, i) => normalizedHeaders[i] === h);
      const hasDataRows = records.length > 0;
      if (!isValidHeaders || !hasDataRows) {
        setCsvFile(null);
        setHeaderError(
          !isValidHeaders
            ? 'アップロードテンプレートが正しくありません。'
            : 'ヘッダーの下にデータ行を1件以上入力してください。',
        );
        return;
      }
      setHeaderError(null);
      const rows: IInviteCodeRow[] = records.map((rec, i) =>
        mapCsvRecordToRow(rec, i),
      );
      await onRegister(rows);
      setCsvFile(null);
      onClose();
    } catch (e) {
      console.error(e);
      notificationMessage({
        message: 'CSVの読み込みに失敗しました。',
        type: NOTIFY_STATUS.ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      wrapClassName='company-csv-upload-modal'
      title='CSVをアップロード'
      open={open}
      onCancel={onClose}
      closable
      closeIcon={
        <CloseOutlined style={{ fontSize: 18, color: '#000' }} />
      }
      centered
      width='fit-content'
      style={{ maxWidth: 440 }}
      footer={[
        <Button key='cancel' onClick={onClose}>
          キャンセル
        </Button>,
        <Button
          key='submit'
          type='primary'
          disabled={!csvFile || isLoading}
          onClick={handleRegister}
        >
          登録
        </Button>,
      ]}
    >
      <p className='text-gray-500 text-sm mb-1'>
        対応形式: .csv | 最大ファイルサイズ: 10MB
      </p>
      <input
        type='file'
        accept='.csv'
        className='hidden'
        id={CSV_INPUT_ID}
        onChange={handleFileChange}
      />
      {!csvFile ? (
        <Button
          onClick={() => document.getElementById(CSV_INPUT_ID)?.click()}
          className='border border-gray-300 mt-1'
        >
          ファイルを選択
        </Button>
      ) : (
        <div>
          <div className='text-[13px] weight-400 mt-1'>アップロードファイル</div>
          <div className='relative mt-1 border border-gray-300 rounded px-2 py-1 pr-8 text-[13px] weight-400'>
            <span className='block break-all'>{csvFile.name}</span>
            <button
              type='button'
              aria-label='remove file'
              className='csv-file-remove-btn absolute top-1 right-2 text-gray-500 hover:text-red-500'
              onClick={() => {
                setCsvFile(null);
                setHeaderError(null);
              }}
            >
              <CloseOutlined />
            </button>
          </div>
        </div>
      )}
      {headerError && (
        <div className='text-red-500 text-[12px] mt-2'>
          {headerError}
        </div>
      )}
    </Modal>
  );
};
